import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getKycListRequest,
  getKycHistoryRequest,
  approveRejectDocRequest,
  revokeKycStatusRequest,
} from "../../../redux/Kyc/kycSlice";
import TableCompo from "../Table/TableCompo";
import {
  Box,
  Typography,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Chip,
  IconButton,
  Button,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useCsvConverter from "../../../hooks/convertToCSV";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import { canShowPermission } from "../Permissions/permissionObject";

const KycList = () => {
  const dispatch = useDispatch();
  const { convertToCsv } = useCsvConverter();
  const kyc = useSelector((state) => state.kyc || {});
  const { data = [], total = 0, loading = false } = kyc;

  const historyData = useSelector((state) => state.kyc.historyData || []);

  const [openHistory, setOpenHistory] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [openFromView, setOpenFromView] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [selectedDocType, setSelectedDocType] = useState("");
  const [mode, setMode] = useState("");
  const [pagination, setPagination] = useState({ page: 0, limit: 5 });
  const [searchText, setSearchText] = useState("");

  // Loader states for individual actions
  const [isProcessingApprove, setIsProcessingApprove] = useState(false);
  const [isProcessingReject, setIsProcessingReject] = useState(false);
  const [isProcessingRevoke, setIsProcessingRevoke] = useState(false);

  const getLatestDocId = (statuses) => {
    return historyData
      .filter(
        (item) =>
          item.docname?.toUpperCase() === selectedDocType?.toUpperCase() &&
          statuses.includes(item.verifyStatus?.toUpperCase()),
      )
      .sort((a, b) => b.createdAt - a.createdAt)[0]?.id;
  };

  useEffect(() => {
    dispatch(
      getKycListRequest({
        page: pagination.page + 1,
        limit: pagination.limit,
        search: searchText,
      }),
    );
  }, [dispatch, pagination, searchText]);

  const handleTableChange = (action, tableState) => {
    if (action === "changePage") {
      setPagination((prev) => ({ ...prev, page: tableState.page }));
    } else if (action === "changeRowsPerPage") {
      setPagination({ page: 0, limit: tableState.rowsPerPage });
    } else if (action === "search") {
      setSearchText(tableState.searchText || "");
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleKycAction = (status) => {
    if (!selectedCustomerId || historyData.length === 0) return;
    const trimmedRemark = rejectRemark.trim();

    if (status === "CANCEL") {
      setIsProcessingRevoke(true);
      const revokeDocId = getLatestDocId(["APPROVED", "REJECTED"]);
      if (!revokeDocId) {
        console.warn("No matching document found for REVOKE");
        setIsProcessingRevoke(false);
        return;
      }

      dispatch(
        revokeKycStatusRequest({
          docId: revokeDocId,
          type: selectedDocType,
          customerId: selectedCustomerId,
        }),
      );

      setTimeout(() => {
        setIsProcessingRevoke(false);
        setOpenHistory(false);
        setOpenFromView(false);
        setRejectMode(false);
        setRejectRemark("");
      }, 1000);
    } else {
      setIsProcessingApprove(status === "APPROVED");
      setIsProcessingReject(status === "REJECTED");

      const actionDocId = getLatestDocId(["PENDING"]);
      if (!actionDocId) {
        console.warn("No matching document found for action:", status);
        setIsProcessingApprove(false);
        setIsProcessingReject(false);
        return;
      }

      dispatch(
        approveRejectDocRequest({
          status,
          rejectremark: status === "REJECTED" ? trimmedRemark : "",
          docno: actionDocId,
        }),
      );

      setTimeout(() => {
        setIsProcessingApprove(false);
        setIsProcessingReject(false);
        setOpenHistory(false);
        setOpenFromView(false);
        setRejectMode(false);
        setRejectRemark("");
      }, 1000);
    }
  };
  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const stored = localStorage.getItem("userPermissions");
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch (e) {
        console.error("permission parse error", e);
      }
    }
  }, []);
  const canCreate = canShowPermission(permissions, "kycVerification", "create");
  const canEdit = canShowPermission(permissions, "kycVerification", "edit");

  const columns = [
    {
      field: "createdAt",
      headerName: "Created At",
      minWidth: 200,
      render: (val, row) =>
        row.createdAt
          ? new Date(row.createdAt).toLocaleString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })
          : "N.A",
    },
    {
      field: "fullName",
      headerName: "Name",
      render: (val, row) => `${row.firstName} ${row.middleName ?? ''} ${row.lastName}`,
    },
    { field: "phone", headerName: "Phone" },
    {
      field: "address",
      headerName: "Address",
      render: (val, row) =>
        row.address?.length
          ? row.address.map((addr, idx) => (
              <div key={idx}>
                {addr.city?.trim() ||
                  addr.state?.trim() ||
                  addr.pincode ||
                  "N/A"}
              </div>
            ))
          : "N/A",
    },
    {
      field: "verifyPAN",
      headerName: "Verify PAN",
      render: (val, row) =>
        row.pan_details?.some((doc) => doc.docname?.toLowerCase() === "pan") ? (
          canEdit ? (
            <Button
              variant="contained"
              sx={{ backgroundColor: "#ffc107", color: "#000" }}
              onClick={() => {
                dispatch(getKycHistoryRequest(row._id));
                setSelectedCustomerId(row._id);
                setSelectedDocType("PAN");
                setMode("PAN");
                setOpenFromView(true);
                setOpenHistory(true);
              }}
            >
              View
            </Button>
          ) : (
            "N.A."
          )
        ) : (
          "N.A."
        ),
    },
    {
      field: "verifyBank",
      headerName: "Verify BANK",
      render: (val, row) =>
        row.bank_details?.length > 0 ? (
          canEdit ? (
            <Button
              variant="contained"
              sx={{ backgroundColor: "#ffc107", color: "#000" }}
              onClick={() => {
                dispatch(getKycHistoryRequest(row._id));
                setSelectedCustomerId(row._id);
                setSelectedDocType("BANK");
                setMode("BANK");
                setOpenFromView(true);
                setOpenHistory(true);
              }}
            >
              View
            </Button>
          ) : (
            "N.A."
          )
        ) : (
          "N.A."
        ),
    },
    {
      field: "kycstatus",
      headerName: "KYC Status",
      render: (val, row) => {
        const status = row.kycstatus;
        let bgColor = "#9e9e9e",
          text = "Unknown";
        if (status === "PENDING") {
          bgColor = "#ffc107";
          text = "Pending";
        } else if (status === "APPROVED") {
          bgColor = "#4caf50";
          text = "Approved";
        } else if (status === "REJECTED") {
          bgColor = "#f44336";
          text = "Rejected";
        }

        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: "#fff",
              padding: "4px 10px",
              borderRadius: "4px",
              fontWeight: "bold",
              fontSize: "13px",
            }}
          >
            {text}
          </span>
        );
      },
    },
    {
      field: "history",
      headerName: "History",
      render: (val, row) => (
        <Button
          variant="contained"
          sx={{ backgroundColor: "#17a2b8", color: "#fff" }}
          onClick={() => {
            dispatch(getKycHistoryRequest(row._id));
            setSelectedCustomerId(row._id);
            setMode("HISTORY");
            setOpenFromView(false);
            setOpenHistory(true);
          }}
        >
          History
        </Button>
      ),
    },
  ];

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadStoreCsv = () => {
    console.log("Store CSV API called");

    dispatch(
      apiRequest({
        method: "GET",
        endpoint: "/api/v1/kyc/kycCsv",
        query: {},
        callback: (response) => {
          console.log("Store CSV response:", response);

          const csv = convertToCsv(response?.data || []);
          triggerCsvDownload(csv, "kyc.csv");
        },
      }),
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100vh", overflow: "auto" }}>
      <Box
        sx={{
          width: "100%",
          mt: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Typography fontWeight={700} fontSize={24} p={2}>
          KYC List
        </Typography>

        <CardContent sx={{ height: "580px", width: "100%" }}>
          <TableCompo
            columns={columns}
            data={JSON.parse(JSON.stringify(data)).reverse()}
            loading={loading}
            onTableChange={handleTableChange}
            totalCount={total}
            page={pagination.page}
            limit={pagination.limit}
            extraButtons={
              <Button
                size="sm"
                variant="outlined"
                onClick={handleDownloadStoreCsv}
              >
                Download CSV
              </Button>
            }
          />
        </CardContent>
      </Box>

      {/* History Dialog */}
      <Dialog
        open={openHistory}
        onClose={() => {
          setOpenHistory(false);
          setOpenFromView(false);
          setRejectMode(false);
          setRejectRemark("");
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Customer Details
          <IconButton
            onClick={() => {
              setOpenHistory(false);
              setOpenFromView(false);
              setRejectMode(false);
              setRejectRemark("");
            }}
            sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {historyData.length === 0 ? (
            <Typography>No history found.</Typography>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Doc No</TableCell>
                    <TableCell>Doc Name</TableCell>
                    {(mode === "HISTORY" ||
                      selectedDocType?.toLowerCase() !== "pan") && (
                      <TableCell>IFSC</TableCell>
                    )}
                    <TableCell>Verified Status</TableCell>
                    <TableCell>Verified By</TableCell>
                    <TableCell>Remark</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const filtered = historyData
                      .filter(
                        (item) =>
                          item.docname?.toLowerCase() ===
                          selectedDocType?.toLowerCase(),
                      )
                      .sort((a, b) => b.createdAt - a.createdAt);

                    const rowsToRender =
                      mode === "HISTORY" ? historyData : filtered.slice(0, 1);

                    return rowsToRender.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          {new Date(item.createdAt).toLocaleString("en-IN")}
                        </TableCell>
                        <TableCell>{`${item.firstName} ${item.middleName ?? ''} ${item.lastName}`}</TableCell>
                        <TableCell>{item.docno}</TableCell>
                        <TableCell>{item.docname}</TableCell>
                        {(mode === "HISTORY" ||
                          selectedDocType?.toLowerCase() !== "pan") && (
                          <TableCell>
                            {mode === "HISTORY"
                              ? item.docname?.toLowerCase() === "pan"
                                ? "N/A"
                                : item.ifsc || "N/A"
                              : item.ifsc || "N/A"}
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            label={item.verifyStatus?.toUpperCase() || "N/A"}
                            size="small"
                            sx={{
                              fontWeight: "bold",
                              color: "#fff",
                              backgroundColor:
                                item.verifyStatus?.toLowerCase() === "pending"
                                  ? "#ffc107"
                                  : item.verifyStatus?.toLowerCase() ===
                                      "rejected"
                                    ? "#f44336"
                                    : item.verifyStatus?.toLowerCase() ===
                                        "cancel"
                                      ? "#9c27b0"
                                      : "#4caf50",
                            }}
                          />
                        </TableCell>
                        <TableCell>{item.verifiedby?.name || "N/A"}</TableCell>
                        <TableCell>{item.rejectremark || "N/A"}</TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>

              {openFromView && (
                <>
                  {rejectMode ? (
                    <Box mt={2}>
                      <TextField
                        label="Reject Remark"
                        value={rejectRemark}
                        onChange={(e) => setRejectRemark(e.target.value)}
                        fullWidth
                        multiline
                        rows={3}
                      />
                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={2}
                        mt={2}
                      >
                        <Button
                          variant="contained"
                          sx={{ backgroundColor: "#f44336" }}
                          onClick={() => handleKycAction("REJECTED")}
                          disabled={!rejectRemark.trim() || isProcessingReject}
                        >
                          {isProcessingReject
                            ? "Processing..."
                            : "Confirm Reject"}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setRejectMode(false);
                            setRejectRemark("");
                          }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      gap={2}
                      mt={2}
                    >
                      {(() => {
                        const latest = historyData
                          .filter(
                            (item) =>
                              item.docname?.toUpperCase() ===
                              selectedDocType?.toUpperCase(),
                          )
                          .sort(
                            (a, b) =>
                              new Date(b.createdAt) - new Date(a.createdAt),
                          )[0];

                        if (latest?.verifyStatus === "PENDING") {
                          return (
                            <>
                              <Button
                                variant="contained"
                                sx={{ backgroundColor: "#4caf50" }}
                                onClick={() => handleKycAction("APPROVED")}
                                disabled={isProcessingApprove}
                              >
                                {isProcessingApprove
                                  ? "Processing..."
                                  : "Approve"}
                              </Button>
                              <Button
                                variant="contained"
                                sx={{ backgroundColor: "#f44336" }}
                                onClick={() => setRejectMode(true)}
                                disabled={isProcessingReject}
                              >
                                Reject
                              </Button>
                            </>
                          );
                        }

                        if (
                          latest?.verifyStatus === "APPROVED" ||
                          latest?.verifyStatus === "REJECTED"
                        ) {
                          return (
                            <Button
                              variant="contained"
                              sx={{ backgroundColor: "#9c27b0" }}
                              onClick={() => handleKycAction("CANCEL")}
                              disabled={isProcessingRevoke}
                            >
                              {isProcessingRevoke ? "Processing..." : "Revoke"}
                            </Button>
                          );
                        }

                        return null;
                      })()}
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default KycList;
